import { useContactInfo } from "../hooks/useContactInfo";
import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Anchor, Clock, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa6";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import JumpingFish from "../components/JumpingFish";
import Bubbles from "../components/Bubbles";
import HoneypotField from "../components/HoneypotField";
import EnquiryConfirmation from "../components/EnquiryConfirmation";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useEnquiryForm from "../hooks/useEnquiryForm";
import { submitContactEnquiry } from "../utils/contactEnquiry";
import {
  getWhatsAppLink,
  CONTACT_ENQUIRY_MESSAGE,
} from "../data/whatsappContext";
import {
  COMPANY_NAME,
} from "../data/legalContact";

export default function Contact() {
  useDocumentTitle("Contact Us");
  const info = useContactInfo();
  const INFO = [
    { icon: Mail, label: "General Enquiries", value: info.email, href: `mailto:${info.email}`, valueClassName: "text-sm break-all" },
    { icon: Clock, label: "Hours", value: info.hours },
  ];
  const { status, referenceNumber, submit, reset } = useEnquiryForm("contact");
  const [honeypot, setHoneypot] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Links from the profile / order pages pre-fill the subject (e.g. /contact?subject=Refund%20assistance).
  const [subject, setSubject] = useState(() => new URLSearchParams(window.location.search).get("subject")?.slice(0, 200) ?? "");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    submit(() =>
      submitContactEnquiry({
        name,
        email,
        subject: subject || undefined,
        message,
      }),
    );
  };

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.address)}`;

  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish
          className="left-[11%] bottom-10 hidden sm:block"
          size={38}
          delay={0.7}
        />
        <JumpingFish
          className="right-[13%] bottom-16 hidden sm:block"
          delay={2.7}
          duration={2.1}
          color="var(--color-coral-400)"
          flip
        />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            We&apos;d love to hear from you
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            General questions, feedback, or just want to talk seafood? Reach out
            below. Partnership, supplier, and B2B enquiries go through our{" "}
            <a
              href="/partnerships"
              className="font-semibold text-teal-300 hover:text-teal-200"
            >
              Partnerships page
            </a>{" "}
            instead, so they reach the right team faster.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr]">
          {/* Info + support channels */}
          <ScrollReveal>
            <div className="rounded-2xl bg-coral-500/10 p-5 text-sm text-coral-700">
              Existing customer with an order issue? Use the in-app support chat
              for the fastest resolution — it's connected directly to your order
              history.
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {INFO.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl bg-foam-100 p-5 ring-1 ring-ocean-900/5 dark:bg-white/5 dark:ring-white/10"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                    <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ocean-800/50 dark:text-foam-100/50">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      className={`mt-1 block font-display font-semibold text-ocean-950 hover:text-teal-600 dark:text-foam-50 ${item.valueClassName ?? ""}`}
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p
                      className={`mt-1 font-display font-semibold text-ocean-950 dark:text-foam-50 ${item.valueClassName ?? ""}`}
                    >
                      {item.value}
                    </p>
                  )}
                </div>
              ))}

              <a
                href={getWhatsAppLink(CONTACT_ENQUIRY_MESSAGE)}
                target="_blank"
                rel="noreferrer"
                className="rounded-2xl bg-foam-100 p-5 ring-1 ring-ocean-900/5 transition-colors hover:bg-foam-200 dark:bg-white/5 dark:ring-white/10 dark:hover:bg-white/10"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                  <FaWhatsapp className="h-5 w-5" />
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ocean-800/50 dark:text-foam-100/50">
                  WhatsApp
                </p>
                <p className="mt-1 font-display font-semibold text-ocean-950 dark:text-foam-50">
                  Tap to chat
                </p>
              </a>

              <div className="rounded-2xl bg-foam-100 p-5 ring-1 ring-ocean-900/5 dark:bg-white/5 dark:ring-white/10">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                  <MessageCircle className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ocean-800/50 dark:text-foam-100/50">
                  In-App Chat
                </p>
                <p className="mt-1 font-display font-semibold text-ocean-950 dark:text-foam-50">
                  Open the ZenFresh app
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl bg-foam-100 p-5 ring-1 ring-ocean-900/5 dark:bg-white/5 dark:ring-white/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                <MapPin className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ocean-800/50 dark:text-foam-100/50">
                Registered Office
              </p>
              <p className="mt-1 font-display font-semibold text-ocean-950 dark:text-foam-50">
                {COMPANY_NAME}
              </p>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block text-sm text-ocean-800/70 hover:text-teal-600 dark:text-foam-100/70"
              >
                {info.address}
              </a>
            </div>

            <div className="relative mt-6 flex h-56 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-ocean-900 to-ocean-700">
              <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle,white_1px,transparent_1px)] [background-size:16px_16px]" />
              <div className="relative flex flex-col items-center text-foam-50">
                <Anchor className="h-8 w-8 text-teal-300" strokeWidth={1.5} />
                <p className="mt-2 text-sm font-medium">
                  Serving 12 coastal cities
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Form */}
          <ScrollReveal delay={0.1}>
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10 sm:p-8">
              <AnimatePresence mode="wait">
                {status === "success" && referenceNumber ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    <EnquiryConfirmation
                      type="general"
                      referenceNumber={referenceNumber}
                      onSendAnother={reset}
                    />
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <HoneypotField value={honeypot} onChange={setHoneypot} />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <label className="block text-sm">
                        <span className="font-medium text-ocean-900 dark:text-foam-100">
                          Name
                        </span>
                        <input
                          required
                          type="text"
                          placeholder="Jane Doe"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="font-medium text-ocean-900 dark:text-foam-100">
                          Email
                        </span>
                        <input
                          required
                          type="email"
                          placeholder="jane@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                        />
                      </label>
                    </div>
                    <label className="block text-sm">
                      <span className="font-medium text-ocean-900 dark:text-foam-100">
                        Subject
                      </span>
                      <input
                        type="text"
                        placeholder="What's this about?"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-ocean-900 dark:text-foam-100">
                        Message
                      </span>
                      <textarea
                        required
                        rows={4}
                        placeholder="Tell us what's on your mind..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="mt-1.5 w-full resize-none rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ocean-900 px-6 py-3 text-sm font-semibold text-foam-50 transition-transform hover:scale-[1.02] disabled:opacity-50 dark:bg-teal-400 dark:text-ocean-950"
                    >
                      {status === "loading" ? "Sending…" : "Send Message"}
                      <Send className="h-4 w-4" />
                    </button>
                    {status === "error" && (
                      <p className="text-center text-xs text-coral-500">
                        Something went wrong — please try again.
                      </p>
                    )}
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}
